import { ArrowBackIcon, Box, Button, Container, Grid, Stack } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import sanitize from "sanitize-html";
import { PublicProfileNewsCard } from "views/Clients/News/PublicProfileNewsCard";

import { MetaTags } from "@/components/MetaTags";
import { fetchNewsItem, fetchPublicProfileInfoBySlug } from "@/data/public-profiles";
import { useFetcher } from "@/hooks/http";
import { IWithRouteMatchProps } from "@/infrastructure/Route";
import { truncateToWholeWords } from "@/utils/strings";
import { router } from "@/views/router";

import { ClientPageHeader } from "../ClientPage/ClientPageHeader";
import { RecentJobsWidget } from "../RecentJobsWidget";
import { transformRouteProps } from "./selectors";

interface ClientNewsPageProps extends IWithRouteMatchProps {
  isPublic?: boolean;
}

export const ClientNewsPage: FC<ClientNewsPageProps> = ({ isPublic, ...restProps }) => {
  const { t } = useTranslation("public");

  const { slug, newsId } = transformRouteProps(restProps);

  const { payload } = useFetcher(fetchNewsItem, { newsId });
  const clientInfoFetcher = useFetcher(fetchPublicProfileInfoBySlug, { slug });

  if (!payload) {
    return null;
  }

  const baseRoute = isPublic ? "public-client" : "client";
  const newsListPath = router.get(`${baseRoute}.news`, { slug: payload.publicProfile.slug }).path;

  return (
    <React.Fragment>
      <MetaTags
        title={t("CLIENT_PAGE.META.NEWS_TITLE", { title: truncateToWholeWords(payload.title, 51) })}
        description={truncateToWholeWords(
          payload.shortText?.length
            ? payload.shortText
            : sanitize(payload.longText, { allowedTags: [], allowedAttributes: {} }),
          51,
        )}
        ogImage={payload.coverImage.publicUrl}
        ogImageType={payload.coverImage.contentType}
      />

      <Stack>
        <ClientPageHeader
          publicProfile={payload.publicProfile}
          forceActiveTab="news"
          info={clientInfoFetcher.payload}
          isPublic={isPublic}
        />

        <Box pt="xs" pb="xxl" backgroundColor={isPublic ? "n0" : undefined}>
          <Container>
            <Box px={[null, null, null, "sm"]}>
              <Stack gap="xs">
                <Box>
                  <Button as={Link} to={newsListPath} size="sm" variant="ghost" iconLeft={<ArrowBackIcon />}>
                    {t("CLIENT_PAGE.NEWS.BUTTON_BACK_TO_NEWS")}
                  </Button>
                </Box>

                <Grid columns={["1fr", null, null, "repeat(12, 1fr)"]}>
                  <Box gridColumnStart={1} gridColumnEnd={[2, null, null, 9]} gridRowStart={1} gridRowEnd={2}>
                    <PublicProfileNewsCard
                      news={payload}
                      publicProfile={payload.publicProfile}
                      isPublic={isPublic}
                      full
                    />
                  </Box>

                  <Box
                    gridColumnStart={[1, null, null, 9]}
                    gridColumnEnd={[1, null, null, 13]}
                    gridRowStart={[2, null, null, 1]}
                    gridRowEnd={[3, null, null, 2]}>
                    <RecentJobsWidget
                      publicProfileIds={[payload.publicProfile.id]}
                      slug={payload.publicProfile.slug}
                      isPublic={isPublic}
                    />
                  </Box>
                </Grid>
              </Stack>
            </Box>
          </Container>
        </Box>
      </Stack>
    </React.Fragment>
  );
};
